import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import NoIndex from "../components/NoIndex";

const FIELDS = [
  { key: "full_name", label: "Full Name", placeholder: "Anthony Playworld" },
  { key: "phone", label: "Phone", placeholder: "+1 555 555 0123" },
  { key: "address_line1", label: "Address Line 1", placeholder: "123 Playworld Lane" },
  { key: "address_line2", label: "Address Line 2", placeholder: "Apt 4B" },
  { key: "city", label: "City", placeholder: "Funtown" },
  { key: "state", label: "State / Region", placeholder: "CA" },
  { key: "postal_code", label: "Postal Code", placeholder: "90210" },
  { key: "country", label: "Country", placeholder: "USA" },
];

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [profile, setProfile] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    api.get("/me/profile", { withCredentials: true })
      .then(({ data }) => setProfile(data || {}))
      .catch(() => setProfile({}))
      .finally(() => setLoaded(true));
  }, [user, loading, navigate]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        payload[f.key] = profile[f.key] || "";
      });
      await api.put("/me/profile", payload, { withCredentials: true });
      toast.success("Profile saved!");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !loaded || !user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center font-display text-2xl">
        Loading...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="account-page">
      <NoIndex title="My Account" />
      <div className="flex items-center gap-4 mb-8">
        {user.picture && (
          <img
            src={user.picture}
            alt={user.name}
            className="w-16 h-16 rounded-full border-4 border-black"
          />
        )}
        <div>
          <h1 className="font-display text-4xl uppercase leading-none">{user.name}</h1>
          <p className="opacity-70 text-sm mt-1">{user.email}</p>
        </div>
      </div>

      <form onSubmit={save} className="pw-card p-6 sm:p-8 space-y-4" data-testid="profile-form">
        <h2 className="font-display text-2xl uppercase mb-2">Shipping & Contact Info</h2>
        <p className="text-sm opacity-70 mb-4">
          We&apos;ll use this to ship your Playworld goodies to the right place.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="font-bold uppercase text-xs tracking-wider block mb-1">
                {f.label}
              </span>
              <input
                type="text"
                value={profile[f.key] || ""}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, [f.key]: e.target.value }))
                }
                placeholder={f.placeholder}
                className="pw-input w-full"
                data-testid={`profile-${f.key}`}
              />
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 pt-4">
          <button
            type="submit"
            className="pw-btn"
            disabled={saving}
            data-testid="profile-save-button"
          >
            {saving ? "Saving..." : "Save Profile"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="pw-btn pw-btn-blue"
            data-testid="profile-orders-button"
          >
            My Orders
          </button>
          <button
            type="button"
            onClick={logout}
            className="pw-btn pw-btn-ghost"
            data-testid="profile-logout-button"
          >
            Sign Out
          </button>
        </div>
      </form>
    </div>
  );
}
